import React, { createContext, useContext, useMemo } from "react";
import { useSeasons } from "./SeasonContext";
import { useAlerts } from "./AlertsContext";
import {
  getHazardId,
  isHazardVisible,
  normalizeHazardId,
} from "../config/hazardConfig";

const ChecklistDefaultsContext = createContext(null);

const severityRank = (type) => {
  if (!type) return 0;
  const t = type.toLowerCase();
  if (t.includes("warning")) return 3;
  if (t.includes("watch")) return 2;
  if (t.includes("burn ban")) return 1;
  return 0;
};

const inferFireWeatherAlertType = (alerts) => {
  let type = null;

  (alerts || [])
    .filter((a) => a.type === "weather")
    .forEach((a) => {
      const props = a.raw?.properties || {};
      const event = (props.event || "").toLowerCase();

      if (event.includes("red flag warning")) {
        type = "Warning";
      } else if (!type && event.includes("fire weather watch")) {
        type = "Watch";
      }
    });

  return type;
};

const inferTornadoAlertType = (alerts) => {
  let type = null;

  (alerts || [])
    .filter((a) => a.type === "weather")
    .forEach((a) => {
      const props = a.raw?.properties || {};
      const event = (props.event || "").toLowerCase();
      const headline = (props.headline || "").toLowerCase();
      const text = `${event} ${headline}`;

      if (text.includes("tornado warning")) {
        type = "Warning";
      } else if (!type && text.includes("tornado watch")) {
        type = "Watch";
      }
    });

  return type;
};

const pushContext = (list, ctx) => {
  if (!ctx?.hazardId) return;

  const normalizedId = normalizeHazardId(ctx.hazardId);
  if (!normalizedId) return;
  if (!isHazardVisible(normalizedId)) return;

  list.push({
    ...ctx,
    hazardId: normalizedId,
  });
};

const dedupeExactContexts = (contexts) => {
  const seen = new Map();

  contexts.forEach((ctx) => {
    const key = [
      normalizeHazardId(ctx.hazardId),
      String(ctx.alertType || "").toLowerCase(),
      String(ctx.source || "").toLowerCase(),
      Number(ctx.priority || 0),
    ].join("__");

    if (!seen.has(key)) {
      seen.set(key, ctx);
    }
  });

  return Array.from(seen.values()).sort((a, b) => {
    const rankDiff = Number(b.priority || 0) - Number(a.priority || 0);
    if (rankDiff !== 0) return rankDiff;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
};

const dedupeContextsByHazardForPrimary = (contexts) => {
  const bestByHazard = new Map();

  contexts.forEach((ctx) => {
    const prev = bestByHazard.get(ctx.hazardId);

    if (!prev || (ctx.priority || 0) > (prev.priority || 0)) {
      bestByHazard.set(ctx.hazardId, ctx);
      return;
    }

    if (
      prev &&
      (ctx.priority || 0) === (prev.priority || 0) &&
      prev.source === "season" &&
      ctx.source === "alert"
    ) {
      bestByHazard.set(ctx.hazardId, ctx);
    }
  });

  return Array.from(bestByHazard.values()).sort((a, b) => {
    const rankDiff = Number(b.priority || 0) - Number(a.priority || 0);
    if (rankDiff !== 0) return rankDiff;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
};

const buildSituationCandidates = (flags, alerts) => {
  const candidates = [];

  if (flags?.hasWinterWarning) {
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "winterWarning",
    });
  } else if (flags?.hasWinterWatch) {
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Watch",
      rank: severityRank("Watch"),
      source: "winterWatch",
    });
  } else if (flags?.hasWinterStorm) {
    candidates.push({
      hazard: "Winter Weather",
      alertType: "Watch",
      rank: severityRank("Watch"),
      source: "winterStorm",
    });
  }

  const fireWeatherType = flags?.hasFireWeather
    ? inferFireWeatherAlertType(alerts) || "Watch"
    : null;

  if (fireWeatherType) {
    candidates.push({
      hazard: "Wildfire",
      alertType: fireWeatherType,
      rank: severityRank(fireWeatherType),
      source: "fireWeather",
    });
  }

  if (flags?.hasBurnBan) {
    candidates.push({
      hazard: "Wildfire",
      alertType: "Burn Ban",
      rank: severityRank("Burn Ban"),
      source: "burnBan",
    });
  }

  const tornadoType = flags?.hasTornado
    ? inferTornadoAlertType(alerts) || "Watch"
    : null;

  if (tornadoType) {
    candidates.push({
      hazard: "Tornado",
      alertType: tornadoType,
      rank: severityRank(tornadoType),
      source: "tornado",
    });
  }

  if (flags?.hasFlooding) {
    candidates.push({
      hazard: "Flooding",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "flooding",
    });
  }

  if (flags?.hasSevereStorm) {
    candidates.push({
      hazard: "Severe Storms",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "severeStorm",
    });
  }

  if (flags?.hasExtremeCold) {
    candidates.push({
      hazard: "Extreme Cold",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "extremeCold",
    });
  }

if (flags?.hasExtremeHeat) {
  candidates.push({
    hazard: "Extreme Heat",
    alertType: "Warning",
    rank: severityRank("Warning"),
    source: "extremeHeat",
  });
}

  if (flags?.hasHighWinds) {
    candidates.push({
      hazard: "High Winds",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "highWinds",
    });
  }

  if (flags?.hasFog) {
    candidates.push({
      hazard: "Dense Fog",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "denseFog",
    });
  }

  if (flags?.hasSurfHazard) {
    candidates.push({
      hazard: "Surf & Rip Currents",
      alertType: "Warning",
      rank: severityRank("Warning"),
      source: "surfRip",
    });
  }

  return candidates.filter((c) => isHazardVisible(c.hazard));
};

const buildPlanningContexts = ({ currentSeasons, flags, alerts }) => {
  const contexts = [];

  (currentSeasons || []).forEach((season) => {
    if (!season?.hazardKey) return;

    const hazardId = getHazardId(season.hazardKey);

    pushContext(contexts, {
      hazardId,
      label: season.hazardKey,
      source: "season",
      alertType: "Seasonal",
      priority: 0,
    });
  });

  if (flags?.hasWinterWarning) {
    pushContext(contexts, {
      hazardId: getHazardId("Winter Weather"),
      label: "Winter Weather",
      source: "alert",
      alertType: "Warning",
      priority: 3,
    });
  } else if (flags?.hasWinterWatch || flags?.hasWinterStorm) {
    pushContext(contexts, {
      hazardId: getHazardId("Winter Weather"),
      label: "Winter Weather",
      source: "alert",
      alertType: "Watch",
      priority: 2,
    });
  }

  const fireWeatherType = flags?.hasFireWeather
    ? inferFireWeatherAlertType(alerts) || "Watch"
    : null;

  if (fireWeatherType) {
    pushContext(contexts, {
      hazardId: getHazardId("Wildfire"),
      label: "Wildfire",
      source: "alert",
      alertType: fireWeatherType,
      priority: fireWeatherType === "Warning" ? 3 : 2,
    });
  }

  if (flags?.hasBurnBan) {
    pushContext(contexts, {
      hazardId: getHazardId("Wildfire"),
      label: "Wildfire",
      source: "alert",
      alertType: "Burn Ban",
      priority: 1,
    });
  }

  const tornadoType = flags?.hasTornado
    ? inferTornadoAlertType(alerts) || "Watch"
    : null;

  if (tornadoType) {
    pushContext(contexts, {
      hazardId: getHazardId("Tornado"),
      label: "Tornado",
      source: "alert",
      alertType: tornadoType,
      priority: tornadoType === "Warning" ? 3 : 2,
    });
  }

  if (flags?.hasFlooding) {
    pushContext(contexts, {
      hazardId: getHazardId("Flooding"),
      label: "Flooding",
      source: "alert",
      alertType: "Warning",
      priority: 3,
    });
  }

  if (flags?.hasSevereStorm) {
    pushContext(contexts, {
      hazardId: getHazardId("Severe Storms"),
      label: "Severe Storms",
      source: "alert",
      alertType: "Warning",
      priority: 3,
    });
  }

  if (flags?.hasExtremeCold) {
    pushContext(contexts, {
      hazardId: getHazardId("Extreme Cold"),
      label: "Extreme Cold",
      source: "alert",
      alertType: "Warning",
      priority: 3,
    });
  }

if (flags?.hasExtremeHeat) {
  pushContext(contexts, {
    hazardId: getHazardId("Extreme Heat"),
    label: "Extreme Heat",
    source: "alert",
    alertType: "Warning",
    priority: 3,
  });
}

  if (flags?.hasHighWinds) {
    pushContext(contexts, {
      hazardId: getHazardId("High Winds"),
      label: "High Winds",
      source: "alert",
      alertType: "Warning",
      priority: 2,
    });
  }

  if (flags?.hasFog) {
    pushContext(contexts, {
      hazardId: getHazardId("Dense Fog"),
      label: "Dense Fog",
      source: "alert",
      alertType: "Warning",
      priority: 2,
    });
  }

  if (flags?.hasSurfHazard) {
    pushContext(contexts, {
      hazardId: getHazardId("Surf & Rip Currents"),
      label: "Surf & Rip Currents",
      source: "alert",
      alertType: "Warning",
      priority: 2,
    });
  }

  const activeContexts = dedupeExactContexts(contexts);
  const activeHazardIds = Array.from(
    new Set(activeContexts.map((c) => c.hazardId).filter(Boolean))
  );

  const primaryContexts = dedupeContextsByHazardForPrimary(contexts);

  return {
    activeContexts,
    activeHazardIds,
    primaryContext: primaryContexts[0] || null,
  };
};

export const ChecklistDefaultsProvider = ({ children }) => {
  const { currentSeasons, defaultSeason } = useSeasons();
  const { flags, flagsReady, alerts } = useAlerts();

  const value = useMemo(() => {
    let seasonalHazard = "Routine";

    if (defaultSeason?.id === "winterWeather") {
      seasonalHazard = "Winter Weather";
    } else if (defaultSeason?.id === "wildfire") {
      seasonalHazard = "Wildfire";
    } else {
      if (currentSeasons.some((s) => s.id === "wildfire")) {
        seasonalHazard = "Wildfire";
      } else if (currentSeasons.some((s) => s.id === "winterWeather")) {
        seasonalHazard = "Winter Weather";
      }
    }

    let defaultHazard = seasonalHazard;
    let defaultAlertType = "Seasonal";

    const { activeContexts, activeHazardIds, primaryContext } =
      buildPlanningContexts({
        currentSeasons,
        flags,
        alerts,
      });

    let candidates = [];
    let hasSpecificAlertDefaults = false;

    if (!flagsReady) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    const hasAnyAlerts = alerts && alerts.length > 0;
    if (!hasAnyAlerts) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    candidates = buildSituationCandidates(flags, alerts);

    if (!candidates.length) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    candidates.sort((a, b) => b.rank - a.rank);
    const best = candidates[0];

    defaultHazard = best.hazard;
    defaultAlertType = best.alertType;
    hasSpecificAlertDefaults = true;

    return {
      defaultHazard,
      defaultAlertType,
      seasonalHazard,
      candidates,
      activeContexts,
      activeHazardIds,
      primaryContext,
      hasSpecificAlertDefaults,
    };
  }, [
    currentSeasons,
    defaultSeason,
    flagsReady,
    flags?.hasWinterStorm,
    flags?.hasWinterWatch,
    flags?.hasWinterWarning,
    flags?.hasBurnBan,
    flags?.hasFireWeather,
    flags?.hasTornado,
    flags?.hasFlooding,
    flags?.hasSevereStorm,
    flags?.hasExtremeCold,
    flags?.hasExtremeHeat,
    flags?.hasHighWinds,
    flags?.hasFog,
    flags?.hasSurfHazard,
    alerts,
  ]);

  return (
    <ChecklistDefaultsContext.Provider value={value}>
      {children}
    </ChecklistDefaultsContext.Provider>
  );
};

export const useChecklistDefaults = () => {
  const ctx = useContext(ChecklistDefaultsContext);
  if (!ctx) {
    throw new Error(
      "useChecklistDefaults must be used within ChecklistDefaultsProvider"
    );
  }
  return ctx;
};